import React, { useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import DndOrImport from '../../../../../Tools/import/DndOrImport';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import ImportKindToggle, { ImportKind } from './ImportKindToggle';
import { buildFileFormData, buildFileImportUrl, isSupportedImportFile } from './pdsImport';
import { PdsImportState } from './usePdsImport';
import '../../../../../Tools/import/import.scss';

type FileTabProps = {
  kind: ImportKind;
  onKindChange: (kind: ImportKind) => void;
  importState: PdsImportState;
};

export default function FileTab({ kind, onKindChange, importState }: FileTabProps) {
  const { t } = useTranslation();
  const { state } = useInterventionCreation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const hasUnsupportedFormat = selectedFile !== null && !isSupportedImportFile(selectedFile);
  const canImport =
    selectedFile !== null &&
    !hasUnsupportedFormat &&
    !importState.isImporting &&
    state.clientId !== null;

  const importFile = () => {
    if (selectedFile === null || state.clientId === null) {
      return;
    }

    importState.importPds({
      url: buildFileImportUrl(
        kind,
        state.clientId,
        Intl.DateTimeFormat().resolvedOptions().timeZone
      ),
      body: buildFileFormData(selectedFile),
      fileName: selectedFile.name
    });
  };

  return (
    <Stack spacing={2}>
      <ImportKindToggle
        inline
        value={kind}
        onChange={onKindChange}
        labelKey="interventionCreation.addServicePoints.fileKind"
      />

      <Stack spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: '100%',
            // .dnd fixe une zone de 300x120 et un rayon de 4px dans import.scss :
            // on l'aligne localement sur la maquette et sur le thème arrondi,
            // plutôt que de modifier une feuille de style partagée.
            '& .dnd': {
              width: '100%',
              height: 200,
              borderRadius: (theme) => `${theme.shape.borderRadius}px`
            }
          }}
        >
          <DndOrImport
            setNewFile={setSelectedFile}
            header={
              <Stack spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1">
                  {t('interventionCreation.addServicePoints.dropHint')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('interventionCreation.addServicePoints.acceptedFormats')}
                </Typography>
              </Stack>
            }
          />
        </Box>

        {selectedFile && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              {t('interventionCreation.addServicePoints.selectedFile', {
                name: selectedFile.name
              })}
            </Typography>
            <IconButton
              size="small"
              aria-label={t('interventionCreation.addServicePoints.removeFile')}
              onClick={() => setSelectedFile(null)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {hasUnsupportedFormat && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('interventionCreation.addServicePoints.unsupportedFormat')}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" textAlign="center">
          {t('interventionCreation.addServicePoints.prequalifiedHint')}
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={!canImport}
          onClick={importFile}
        >
          {t('interventionCreation.addServicePoints.import')}
        </Button>
      </Box>
    </Stack>
  );
}
